import React, { forwardRef, useCallback, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import { useDispatch, useSelector } from "react-redux";
import {
  setProgress,
  setDuration,
  setLoaded,
  pause,
  setCurrentContent,
  playMedia,
} from "../../redux/Slice/playerSlice";
import { useListeningDepthMutation } from "../../api/podcastsApi";

export const HiddenPlayer = forwardRef((_props, ref) => {
  const dispatch = useDispatch();
  const {
    isPlaying,
    currentMediaLink,
    currentContent,
    volume,
    duration,
    progress,
    playbackRate,
  } = useSelector((state) => state.player);

  const [listeningDepth] = useListeningDepthMutation();

  const playlistReleases = useSelector(
    (state) => state.playlistReleases.playlistReleases,
  );

  const currentReleaseIndex = playlistReleases.findIndex(
    (release) => release.id === currentContent?.id,
  );

  const handleProgress = (state) => {
    dispatch(setLoaded(state.loadedSeconds));
    dispatch(setProgress(state.playedSeconds));
  };

  const handleDuration = (durationTime) => {
    dispatch(setDuration(durationTime));
  };

  const progressRef = useRef(progress);
  const initialTimestampRef = useRef(null);
  const hasSetInitialPositionRef = useRef(false);

  useEffect(() => {
    progressRef.current = progress;
  }, [progress]);

  useEffect(() => {
    // Сбрасываем initialTimestamp при смене выпуска подкаста
    initialTimestampRef.current = null;
    hasSetInitialPositionRef.current = false;
  }, [currentContent]);

  const seekForward = useCallback(() => {
    if (ref.current) {
      const newTime = progressRef.current + 5;
      ref.current.seekTo(newTime, "seconds");
      dispatch(setProgress(newTime));
    }
  }, [dispatch, ref]);

  const seekBackward = useCallback(() => {
    if (ref.current) {
      const newTime = progressRef.current - 5;
      ref.current.seekTo(newTime, "seconds");
      dispatch(setProgress(newTime));
    }
  }, [dispatch, ref]);

  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case "ArrowRight":
          seekForward();
          break;
        case "ArrowLeft":
          seekBackward();
          break;
        default:
          break;
      }
    },
    [seekForward, seekBackward],
  );

  // Добавляем обработчик событий клавиатуры
  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  useEffect(() => {
    let intervalId;
    if (isPlaying && currentContent) {
      // Устанавливаем начальный timestamp при первом воспроизведении
      if (!initialTimestampRef.current) {
        initialTimestampRef.current = Date.now();
      }

      intervalId = setInterval(() => {
        const body = {
          duration: parseInt(duration),
          listening_depth: parseInt(progressRef.current),
          podcast_id: currentContent.podcastId,
          podcast_track_id: currentContent.id,
          timestamp: initialTimestampRef.current,
        };
        listeningDepth(body);
      }, 15000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isPlaying, currentContent, duration, listeningDepth]);

  const handleReady = useCallback(() => {
    if (ref.current && progress > 0 && !hasSetInitialPositionRef.current) {
      ref.current.seekTo(progress, "seconds");
      hasSetInitialPositionRef.current = true;
    }
  }, [ref, progress]);

  return (
    <ReactPlayer
      ref={ref}
      url={currentMediaLink}
      playing={isPlaying}
      volume={volume}
      playbackRate={playbackRate}
      onProgress={handleProgress}
      onReady={handleReady}
      onDuration={handleDuration}
      onEnded={() => {
        if (currentReleaseIndex === playlistReleases.length - 1) {
          dispatch(pause());
          return;
        }

        dispatch(
          playMedia(playlistReleases[currentReleaseIndex + 1].audioFileLink),
        );
        dispatch(setCurrentContent(playlistReleases[currentReleaseIndex + 1]));
      }}
      progressInterval={100}
      style={{ display: "none" }}
    />
  );
});

HiddenPlayer.displayName = "HiddenPlayer";
